import type { Category, Product } from './database.types';

/**
 * Boisson incluse au menu (parité legacy Big Burger).
 * Quand le client choisit un format « menu » (toute valeur contenant « boisson » —
 * ex. « Boisson » ou « Boisson et frite »), on lui propose une boisson à choisir.
 * Décision produit : la liste des boissons se remplit AUTOMATIQUEMENT depuis la
 * catégorie « Boissons » du magasin (produits disponibles), jamais en dur.
 *
 * Le groupe d'options porteur est identifié par le slug conventionnel `boisson_menu`
 * (cf. CLIENT_OPTION_ORDER / defaultOptions). Tout est pur ici → 100 % testable.
 */

/** Slug conventionnel du groupe « boisson incluse au menu » (auto-rempli). */
export const BOISSON_MENU_SLUG = 'boisson_menu';

/** Mot-clé déclencheur : le sous-choix boisson s'affiche si la valeur choisie le contient. */
export const MENU_DRINK_TRIGGER = 'boisson';

/** Normalise un texte pour comparaison (minuscules, sans accents, trim). */
export function normalizeText(s: string | null | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/** Une catégorie est-elle une catégorie de boissons ? (nom : boisson/drink/soft/soda…) */
const DRINK_CATEGORY_RE = /boisson|drink|soft|soda|canette|bouteille/;
export function isDrinkCategoryName(name: string | null | undefined): boolean {
  return DRINK_CATEGORY_RE.test(normalizeText(name));
}

export interface DrinkValue {
  label: string;
  priceDeltaCents: number;
  isDefault: boolean;
}

/**
 * Repositionne le groupe « boisson au menu » JUSTE APRÈS son option déclencheuse
 * (le groupe Menu/format dont une valeur contient « boisson »), au lieu de le
 * laisser en fin de liste. Plus lisible pour le client (parité legacy : Format →
 * Boisson menu → …). Pur & générique (n'importe quel type de groupe {slug, values}).
 * Si aucun déclencheur trouvé, l'ordre est laissé inchangé.
 */
export function placeMenuDrinkAfterTrigger<
  T extends { slug: string; values: { label: string }[] },
>(groups: T[]): T[] {
  const drinkIdx = groups.findIndex((g) => g.slug === BOISSON_MENU_SLUG);
  if (drinkIdx === -1) return groups;
  const triggerIdx = groups.findIndex(
    (g, i) =>
      i !== drinkIdx &&
      g.values.some((v) => normalizeText(v.label).includes(MENU_DRINK_TRIGGER)),
  );
  if (triggerIdx === -1) return groups;
  const out = groups.slice();
  const [drink] = out.splice(drinkIdx, 1);
  // Après retrait, l'index du déclencheur se décale si le drink était avant lui.
  const insertAt = (drinkIdx < triggerIdx ? triggerIdx - 1 : triggerIdx) + 1;
  out.splice(insertAt, 0, drink);
  return out;
}

/**
 * Construit les valeurs du sous-choix « boisson au menu » à partir du magasin :
 * produits DISPONIBLES rangés dans une catégorie de boissons, triés par ordre
 * (catégorie puis produit puis nom), dédoublonnés par nom. Supplément 0 (inclus).
 */
export function buildMenuDrinkValues(
  categories: Pick<Category, 'id' | 'name' | 'position'>[],
  products: Pick<Product, 'name' | 'category_id' | 'is_available' | 'position'>[],
): DrinkValue[] {
  const drinkCatIds = new Set(
    categories.filter((c) => isDrinkCategoryName(c.name)).map((c) => c.id),
  );
  if (drinkCatIds.size === 0) return [];
  const catPos = new Map(categories.map((c) => [c.id, c.position ?? 0]));
  const drinks = products
    .filter((p) => p.is_available && drinkCatIds.has(p.category_id))
    .sort(
      (a, b) =>
        (catPos.get(a.category_id) ?? 0) - (catPos.get(b.category_id) ?? 0) ||
        a.position - b.position ||
        a.name.localeCompare(b.name),
    );
  const seen = new Set<string>();
  const out: DrinkValue[] = [];
  for (const p of drinks) {
    const key = normalizeText(p.name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ label: p.name, priceDeltaCents: 0, isDefault: false });
  }
  return out;
}
